import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ArrowRight, MapPin, CaretLeft, CaretRight } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import CategoryBadges from '../components/CategoryBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITIES = [
  'Delhi', 'Mumbai', 'Bangalore',
  'Chennai', 'Hyderabad', 'Pune',
  'Kolkata', 'Ahmedabad', 'Jaipur',
  'Chandigarh', 'Kochi', 'Lucknow'
];

const Homepage = () => {
  const [featured, setFeatured] = useState([]);
  const carouselRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    axios.get(`${API}/tournaments?status=approved`).then(({ data }) => {
      setFeatured(data.slice(0, 8));
    }).catch(() => {});
  }, []);

  const scrollCarousel = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const handleScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    const step = card ? card.getBoundingClientRect().width + 24 : 320;
    setActiveSlide(Math.round(el.scrollLeft / step));
  };

  return (
    <>
      <SEO
        title="Beyond Ball India | Discover Basketball Tournaments Across India"
        description="India's home for basketball tournaments. Discover, register, and play 1v1, 3v3 and 5v5 tournaments across Delhi, Mumbai, Bangalore and every Indian city."
        path="/"
      />

      {/* ============ HERO ============
          Compact headline-first hero. 8px grid: pt-24 (96), pb-24 (96).
      */}
      <section className="relative pt-24 sm:pt-28 pb-20 sm:pb-24 overflow-x-clip bg-[#F8F4ED] bg-grain">
        {/* Faint court-arc accent */}
        <svg
          aria-hidden="true"
          viewBox="0 0 1200 240"
          className="absolute left-1/2 -translate-x-1/2 -bottom-10 w-[150%] max-w-none pointer-events-none"
        >
          <path d="M0,240 Q600,40 1200,240" fill="none" stroke="#0F1A2E" strokeOpacity="0.06" strokeWidth="1.5" />
          <path d="M0,240 Q600,110 1200,240" fill="none" stroke="#0F1A2E" strokeOpacity="0.04" strokeWidth="1" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Headline + supporting copy + CTAs */}
          <div className="relative text-center max-w-2xl mx-auto">
            <h1
              className="text-[36px] sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F1A2E] leading-[1.02]"
              data-testid="hero-title"
            >
              Every court. Every city. Every baller.
            </h1>

            {/* 24px between headline and description */}
            <p className="mt-6 text-base sm:text-lg text-[#0F1A2E]/65 leading-relaxed">
              From neighbourhood 3v3s to state-level tournaments — discover, register, and play with the Indian basketball community.
            </p>

            {/* 32px between description and buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto sm:max-w-none">
              <Link
                to="/tournaments"
                className="inline-flex items-center justify-center gap-2 bg-[#0F1A2E] hover:bg-[#1A2746] text-white px-8 py-4 rounded-full text-base font-semibold transition-colors shadow-[0_10px_28px_-12px_rgba(15,26,46,0.45)]"
                data-testid="hero-discover-btn"
              >
                Browse Tournaments
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/how-to-list"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#0F1A2E] text-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-white px-8 py-4 rounded-full text-base font-semibold transition-colors"
                data-testid="hero-list-btn"
              >
                I&apos;m an Organizer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CURVED DIVIDER ============ */}
      <div className="relative -mt-1 leading-none" aria-hidden="true">
        <svg viewBox="0 0 1200 80" preserveAspectRatio="none" className="w-full h-10 sm:h-14 block">
          <path d="M0,80 Q300,0 600,40 T1200,40 L1200,80 Z" fill="#FFFFFF" />
        </svg>
      </div>

      {/* ============ FEATURED TOURNAMENTS ============
          8px grid: pt-16 / pb-24
      */}
      {featured.length > 0 && (
        <section className="relative pt-4 sm:pt-6 pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-center text-[20px] sm:text-3xl font-extrabold uppercase text-[#0F1A2E] mb-12"
              style={{ letterSpacing: '0.14em' }}
              data-testid="featured-tournaments-title"
            >
              Featured Tournaments
            </h2>

            <div className="relative">
              <button
                onClick={() => scrollCarousel(-1)}
                aria-label="Previous"
                className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-[0_12px_28px_-8px_rgba(15,26,46,0.30)] text-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-white transition-colors items-center justify-center"
                data-testid="featured-carousel-prev"
              >
                <CaretLeft size={18} weight="bold" />
              </button>

              <div
                ref={carouselRef}
                onScroll={handleScroll}
                className="flex gap-6 overflow-x-auto hide-scrollbar scroll-snap-x pb-4 pt-2 px-1"
              >
                {featured.map((t, i) => {
                  const isActive = i === activeSlide;
                  return (
                    <Link
                      key={t.tournament_id}
                      to={`/tournaments/${t.tournament_id}`}
                      data-card
                      className={`feat-card snap-start flex-shrink-0 w-[80%] sm:w-[58%] lg:w-[44%] max-w-[520px] bg-white rounded-[32px] overflow-hidden border border-[#0F1A2E]/5 shadow-[0_22px_44px_-22px_rgba(15,26,46,0.22)] ${
                        isActive ? 'scale-[1.015]' : 'scale-100 opacity-95'
                      }`}
                      data-testid={`featured-tournament-${i}`}
                    >
                      <div className="relative aspect-[16/9] bg-gradient-to-br from-orange-100 to-orange-50 overflow-hidden">
                        {t.banner_url ? (
                          <img
                            src={t.banner_url}
                            alt={t.name}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: `${t.banner_focus_x ?? 50}% ${t.banner_focus_y ?? 50}%` }}
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-6xl font-bold text-orange-200">{t.format}</span>
                          </div>
                        )}
                      </div>
                      {/* Unified 24px padding */}
                      <div className="relative p-6">
                        <h3 className="font-bold text-lg sm:text-xl text-[#0F1A2E] mb-1.5 line-clamp-1 pr-20">{t.name}</h3>
                        <div className="text-sm text-[#0F1A2E]/60 mb-3">
                          {t.city}{t.state ? `, ${t.state}` : ''}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <CategoryBadges
                            ages={t.age_categories || []}
                            genders={t.gender_categories || []}
                          />
                        </div>
                        <div className="absolute right-6 bottom-6 px-3 py-1 rounded-full bg-[#FF6A00] text-white text-[11px] font-bold uppercase tracking-wider shadow-[0_10px_18px_-8px_rgba(255,106,0,0.55)]">
                          {t.format}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <button
                onClick={() => scrollCarousel(1)}
                aria-label="Next"
                className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-[0_12px_28px_-8px_rgba(15,26,46,0.30)] text-[#0F1A2E] hover:bg-[#0F1A2E] hover:text-white transition-colors items-center justify-center"
                data-testid="featured-carousel-next"
              >
                <CaretRight size={18} weight="bold" />
              </button>

              <div className="mt-8 flex items-center justify-center gap-2" data-testid="carousel-dots">
                {featured.map((t, i) => (
                  <span
                    key={t.tournament_id || `slide-${i}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeSlide ? 'w-7 bg-[#FF6A00]' : 'w-1.5 bg-[#0F1A2E]/20'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ PIN DROP / CITIES (tightened) ============
          py-16 instead of py-20 — 8px grid */}
      <section className="relative py-16 bg-[#F8F4ED] bg-grain">
        <svg aria-hidden="true" viewBox="0 0 600 600" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] max-w-none opacity-[0.045] pointer-events-none">
          <circle cx="300" cy="300" r="200" fill="none" stroke="#0F1A2E" strokeWidth="1.5" />
          <circle cx="300" cy="300" r="70" fill="none" stroke="#0F1A2E" strokeWidth="1.5" />
        </svg>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="text-xs font-bold tracking-[0.32em] uppercase text-[#FF6A00] mb-3" data-testid="pin-drop-label">
              Pin Drop
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-[#0F1A2E]">
              Hooping happens<br className="sm:hidden" /> everywhere
            </h2>
            <p className="mt-3 text-[#0F1A2E]/65 text-base sm:text-lg">
              From Delhi to Kochi — pick a city, find your court.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3" data-testid="city-pills">
            {CITIES.map((c) => (
              <Link
                key={c}
                to={`/tournaments/city/${c.toLowerCase()}`}
                className="city-pill inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-[#0F1A2E]/12 bg-white text-[#0F1A2E] text-sm font-semibold hover:bg-[#0F1A2E] hover:text-white hover:border-[#0F1A2E]"
                data-testid={`city-link-${c.toLowerCase()}`}
              >
                <MapPin size={14} weight="bold" />
                {c}
              </Link>
            ))}
          </div>

          <div className="mt-12 flex items-center justify-center gap-3 sm:gap-5 text-[10px] sm:text-xs font-bold tracking-[0.34em] uppercase text-[#0F1A2E]/55" data-testid="brand-mantra">
            <span>Discover</span>
            <span className="text-[#FF6A00]">·</span>
            <span>Compete</span>
            <span className="text-[#FF6A00]">·</span>
            <span>Grow</span>
            <span className="text-[#FF6A00]">·</span>
            <span>Belong</span>
          </div>
        </div>
      </section>
    </>
  );
};

export default Homepage;
